import type { ApolloClient, MaybeMasked, OperationVariables } from "@apollo/client";
import { apolloDataInvariant } from "./apolloDataInvariant";

/**
 * Runs a route loader's page-load query and applies `apolloDataInvariant` to
 * the result: the data (partial where an entity was withheld — null content
 * means WITHHELD), or a thrown route response for a whole-operation error.
 *
 * The helper exists to own the one precondition the invariant cannot check
 * for itself: the query runs with `errorPolicy: "all"`, so a response that
 * pairs partial data with errors is delivered for the invariant to judge.
 * Under the default policy the client rejects before anything can strip an
 * entity-withheld denial, and the rejection lands at the route error boundary
 * instead. Callers pass every other option as for `client.query`; the policy
 * is not theirs to set, and the type refuses it.
 */
export async function loaderQuery<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  client: ApolloClient,
  options: ApolloClient.QueryOptions<TData, TVariables> & { errorPolicy?: never },
): Promise<MaybeMasked<TData> | undefined> {
  const result = await client.query<TData, TVariables>({ ...options, errorPolicy: "all" });
  return apolloDataInvariant(result);
}
